import FormVersioningPanel from 'components/IAP/femaCards/FormVersioningPanel';
import { buildFormCompletion } from 'components/IAP/femaCards/formSignatureCompletion';
import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';
import { ResourceRequest } from 'components/IAP/hooks/incidentResourceRequestHooks';

import { ICS213RR_BASE_ID } from './ics213rrSignatureSlots';

type Ics213RrVersioningProps = {
  request: ResourceRequest;
  readOnly?: boolean;
};

export function Ics213RrVersioning({
  request,
  readOnly = false,
}: Ics213RrVersioningProps) {
  const { periodNumber } = useCurrentPeriod();

  // Seat permissions are not enforced for 213RR for now; FormVersioningPanel
  // still only offers Sign for the next open slot (in-order).
  return (
    <FormVersioningPanel
      baseId={ICS213RR_BASE_ID}
      periodNumber={periodNumber}
      formInstanceId={request.resource_request_id}
      readOnly={readOnly}
      signDisabledReason={readOnly ? 'View only' : null}
    />
  );
}

export function use213rrVersionLocked(requestId: string | undefined) {
  const { periodNumber } = useCurrentPeriod();
  const { data: tip } = useFormVersionTip(
    ICS213RR_BASE_ID,
    periodNumber,
    requestId
  );
  return (tip?.signatures?.length ?? 0) > 0;
}

/**
 * Row label from the version when it has loaded, otherwise from the stored
 * status.
 */
export function use213rrStatusLabel(
  requestId: string | undefined,
  storedStatus: string
) {
  const { periodNumber } = useCurrentPeriod();
  const { data: tip, isSuccess } = useFormVersionTip(
    ICS213RR_BASE_ID,
    periodNumber,
    requestId
  );
  if (!isSuccess || !tip?.signatureSlots?.length) {
    return resourceRequestStatusLabel(storedStatus);
  }
  const completion = buildFormCompletion(tip.signatureSlots, tip.signatures);
  if (completion.status === 'signed') return 'Signed';
  if (completion.status === 'partially-signed') return 'Partially signed';
  return 'Unsigned draft';
}

const LEGACY_PARTIAL = new Set([
  'awaiting_approval',
  'awaiting_review',
  'awaiting_order',
  'awaiting_payment',
  'awaiting_final',
  'awaiting_incident_commander',
]);

export function resourceRequestStatusLabel(status: string) {
  switch (status) {
    case 'draft':
      return 'Unsigned draft';
    case 'partially_signed':
      return 'Partially signed';
    case 'completed':
      return 'Signed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return LEGACY_PARTIAL.has(status) ? 'Partially signed' : status;
  }
}

/**
 * Bucket a stored status into the three signature states used by the list
 * filter.
 */
export function resourceRequestFilterStatus(status: string) {
  if (
    status === 'draft' ||
    status === 'partially_signed' ||
    status === 'completed' ||
    status === 'cancelled'
  ) {
    return status;
  }
  return LEGACY_PARTIAL.has(status) ? 'partially_signed' : status;
}
